import {yupResolver} from "@hookform/resolvers/yup";
import sortBy from "lodash/sortBy";
import {useCallback, useEffect, useRef} from "react";
import {useForm} from "react-hook-form";
import * as Yup from "yup";

import {randomColorPreset} from "@/components/colorPicker/ColorPicker.tsx";
import {ANALYZER_MAX_TAGS_PER_CHECK} from "@/pages/analyzer/Analyzer.constants.ts";
import {
  AnalyzerCreateFormValues,
  UseAnalyzerCreateFormOptions,
  UseAnalyzerCreateFormReturn,
} from "@/pages/analyzer/Analyzer.types.ts";
import {buildDefaultTagName} from "@/pages/analyzer/Analyzer.utils.ts";

const AnalyzerCreateSchema = Yup.object().shape({
  tags: Yup.array()
    .of(
      Yup.object().shape({
        id: Yup.number(),
        tag: Yup.string().required(),
        color_hex: Yup.string().required(),
      })
    )
    .max(ANALYZER_MAX_TAGS_PER_CHECK, `A Check is limited to ${ANALYZER_MAX_TAGS_PER_CHECK} Tags.`)
    .required(),
  contactGroups: Yup.array().of(Yup.number().required()).required(),
});

export const useAnalyzerCreateForm = ({
  tags,
  contactGroups,
  analyzedDomain,
  timezone,
}: UseAnalyzerCreateFormOptions): UseAnalyzerCreateFormReturn => {
  const methods = useForm<AnalyzerCreateFormValues>({
    resolver: yupResolver<AnalyzerCreateFormValues>(AnalyzerCreateSchema),
    defaultValues: {tags: [], contactGroups: []},
  });
  const initializedRef = useRef(false);

  useEffect(() => {
    if (initializedRef.current || !tags || !contactGroups) {
      return;
    }
    initializedRef.current = true;
    methods.reset({
      tags: [
        {
          tag: buildDefaultTagName(
            analyzedDomain,
            timezone,
            tags.map(({tag}) => tag.toLowerCase())
          ),
          color_hex: randomColorPreset(),
        },
      ],
      contactGroups: sortBy(contactGroups, "id")
        .slice(0, 1)
        .map(({id}) => id),
    });
  }, [tags, contactGroups, analyzedDomain, timezone, methods]);

  const resetCreateForm = useCallback(() => {
    initializedRef.current = false;
    methods.reset({tags: [], contactGroups: []});
  }, [methods]);

  return {methods, resetCreateForm};
};
